import axios from 'axios'
import { apiBus } from './bus'
import { castArray, has, ii, isPlainObject, run, traverse } from 'vtils'
import {
  decryptHttpResponseDataIfNeeded,
  encryptHttpRequestData,
  prepareRequestHeaders,
  setP,
} from './encrypt'
import { Dialog, Toast } from 'vant'
import { dr } from './dr'
import { globalModel } from '@/model'
import { Method, RequestFunctionParams } from 'yapi-to-typescript'
import { showDialog } from '@/utils'

const DEFAULT_TIMEOUT = 10000
const DEFAULT_RETRY_COUNT = 0
const DEFAULT_TIMEOUT_MULTIPLY = 2

export default async function request<TResponseData>(
  payload: RequestFunctionParams,
  options?: {
    noEncrypt?: boolean
    useCdnData?: string
    noErrorToast?: boolean
    ignore403?: boolean
    endpoint?: string
    timeout?: number
    retryCount?: number
    errorMessageMap?: Record<string, number | number[]>
    mock?: TResponseData
    noP?: boolean
    hasStoreCode?: boolean
    businessChannel?: string
    name?: string
  },
): Promise<TResponseData> {
  if (options?.mock) {
    return options.mock
  }

  if (globalModel.state.isTV && !globalModel.state.isTVSimulate) {
    payload.path = payload.path.replace(/^\/order\//, '/dcbMall/order/')
  }

  // 超时时间
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT

  // 重试次数
  const retryCount = options?.retryCount ?? DEFAULT_RETRY_COUNT

  // 重试函数
  const retry = (reset = false) =>
    request<TResponseData>(payload, {
      ...options,
      ...(reset
        ? {
            timeout: DEFAULT_TIMEOUT,
            retryCount: DEFAULT_RETRY_COUNT,
          }
        : {
            timeout: Math.round(timeout * DEFAULT_TIMEOUT_MULTIPLY),
            retryCount: Math.max(retryCount - 1, 0),
          }),
    })

  // 是否开发环境
  const isDevelopment =
    process.env.NODE_ENV === 'development' ||
    process.env.VUE_APP_RUN_ENV === 'development'

  // 是否测试环境
  const isTest = process.env.VUE_APP_RUN_ENV === 'test'

  // 是否生产环境
  // const isProduction = process.env.VUE_APP_RUN_ENV === 'production'

  // 是否加密请求
  const noEncrypt = !!(
    !(globalModel.state.isTV && !globalModel.state.isTVSimulate) &&
    (options?.noEncrypt || isDevelopment || isTest)
  )

  // 是否使用 CDN 数据
  const useCdnData = !!(options?.useCdnData /** && isProduction */)

  // API 终端
  const apiEndpoint: string =
    (globalModel.state.isTVSimulate &&
      (isDevelopment || isTest
        ? 'https://mall-gw.duotest.cn/'
        : 'https://mall-gw.gogpay.cn/')) ||
    options?.endpoint ||
    ii(() => {
      // prettier-ignore
      const map: Record<string, string> = {
        // evaluation: 'https://dcb-cors.duocai.online/http://10.10.3.5:60011/',
        // writeOffCommodity: 'https://dcb-cors.duocai.online/http://10.10.3.5:60014/',
        // writeOffOrderSaleAfter: 'https://dcb-cors.duocai.online/http://10.10.3.4:60014/',
        // writeOff: 'https://dcb-cors.duocai.online/http://10.10.3.4:60002/',
        // joint: 'https://dcb-cors.duocai.online/http://10.10.3.5:60014/',
        // basicThirdApi: 'https://h5p-gw.gogpay.cn/',
      }
      for (const key of Object.keys(map)) {
        if (payload.path.includes(key)) {
          return map[key]
        }
      }
      return process.env.VUE_APP_API_ENDPOINT!
    })

  // 基本地址
  const baseUrl = `${isDevelopment ? '' : ''}${apiEndpoint.replace(/\/+$/, '')}`

  // 请求地址
  const url = useCdnData
    ? `${options!.useCdnData}?v=${Date.now()}`
    : `${baseUrl}${payload.path}`

  // 请求方式
  const method = useCdnData ? Method.GET : payload.method

  // 请求数据
  const requestData = payload.hasFileData
    ? payload.getFormData()
    : method === Method.POST
    ? await encryptHttpRequestData(payload.data || {}, noEncrypt)
    : {}

  // 请求头
  const requestHeaders = useCdnData
    ? {}
    : await prepareRequestHeaders(
        payload.path.replace(/^\/[^/]+/, ''),
        noEncrypt,
        options?.noP,
        options?.hasStoreCode,
        options?.businessChannel,
      )

  // 具体请求逻辑
  const [err, res] = await run(() =>
    dr.send({
      url: url,
      method: method,
      validateStatus: () => true,
      timeout: timeout,
      headers: requestHeaders,
      data: requestData,
      timeoutErrorMessage: `请求超时: ${payload.path}`,
      name: `${payload.extraInfo.title}${
        options?.name ? `[${options.name}]` : ''
      } · ${payload.extraInfo.author}`,
    }),
  )
  if (
    err &&
    axios.isAxiosError(err) &&
    err.message.includes('请求超时') &&
    retryCount > 0
  ) {
    return retry()
  }
  if (err) {
    if (!options?.noErrorToast) {
      const errorMessage = String(err)
      Toast.clear(true)
      Toast({
        message: errorMessage.includes('请求超时')
          ? '网络不佳，请检查您的网络环境'
          : errorMessage,
        forbidClick: false,
        mask: false,
      })
    }
    throw err
  }
  if (res.headers?.date) {
    globalModel.update({
      serverTimeDiff:
        new Date(res.headers.date).getTime() - new Date().getTime(),
    })
  }
  if (!res.status || res.status < 200 || res.status >= 300) {
    const errorMessage = `<${res.status || 0}> ${
      res?.data?.msg || res.statusText || '未知错误'
    }`
    // globalModel.state.isDebug &&
    //   console.log(
    //     `%c${isVConsole ? ' ' : ''}${payload.extraInfo.title}${
    //       options?.name ? `[${options.name}]` : ''
    //     } · ${payload.extraInfo.author}`,
    //     `
    //       background: linear-gradient(131deg, #ff7e43 0%, #ff111c 100%);
    //       color: #ffffff;
    //       padding: 4px 8px;
    //       border-radius: 4px;
    //       line-height: 1;
    //       white-space: nowrap;
    //       margin-bottom: 4px;
    //     `,
    //     `${isVConsole ? '\n' : ''}\n请求耗时：${(Date.now() - start) / 1000}秒`,
    //     `\n请求地址：${url}`,
    //     `\n请求参数：`,
    //     payload.data,
    //     `\n返回结果：`,
    //     errorMessage,
    //   )
    Toast.clear(true)
    const action = await Dialog({
      title: '网络请求出错',
      message: errorMessage,
      showCancelButton: true,
      cancelButtonText: '取消',
      confirmButtonText: '重试',
    })
    if (action === 'confirm') {
      return retry(true)
    }
    throw new Error(res.statusText)
  }
  const data = await decryptHttpResponseDataIfNeeded(res.data)
  // globalModel.state.isDebug &&
  //   console.log(
  //     `%c${isVConsole ? ' ' : ''}${payload.extraInfo.title}${
  //       options?.name ? `[${options.name}]` : ''
  //     } · ${payload.extraInfo.author}`,
  //     `
  //       background: linear-gradient(131deg, #ff7e43 0%, #ff111c 100%);
  //       color: #ffffff;
  //       padding: 4px 8px;
  //       border-radius: 4px;
  //       line-height: 1;
  //       white-space: nowrap;
  //       margin-bottom: 4px;
  //     `,
  //     `${isVConsole ? '\n' : ''}\n请求耗时：${(Date.now() - start) / 1000}秒`,
  //     `\n请求地址：${url}`,
  //     `\n请求参数：`,
  //     payload.data,
  //     `\n返回结果：`,
  //     data,
  //   )
  if (isPlainObject(data)) {
    // 微信下走弹窗登录
    if (
      !options?.ignore403 &&
      globalModel.state.inWechatWebView &&
      // Need login again
      (data.status === 403 ||
        // p参数解析异常!
        data.status === -1005 ||
        // p参数已过期，或p参数为空!
        data.status === -1006 ||
        // p参数为空!
        data.status === -1007 ||
        // TV 端
        data.status === 401401)
    ) {
      // TV 端
      if (globalModel.state.isTV) {
        // eslint-disable-next-line no-undef
        // @ts-ignore
        unifyQrCode.tool.tvH5token = null
        return retry()
      }

      // 如果在小程序中
      if (globalModel.state.inWeapp) {
        // 先弹个窗询问下用户意愿
        showDialog({
          title: '提示',
          message: '您尚未登录，无法继续当前操作。',
          confirmText: '去登录',
          cancelText: '暂不登录',
          onConfirm: () => {
            // 获取当前地址
            let currentUrl = location.href
            // 强制给当前地址拼接 isAuth=true 以触发小程序登录
            if (!currentUrl.includes('isAuth=true')) {
              currentUrl += `${currentUrl.includes('?') ? '&' : '?'}isAuth=true`
            }
            // 组装小程序中间页路径
            const path = `/pages/dcb-jssdk/navigateTo?payload=${encodeURIComponent(
              JSON.stringify({
                url: currentUrl,
              }),
            )}`
            // 跳转小程序中间页
            // @ts-ignore
            wx.miniProgram.redirectTo({
              url: path,
            })
          },
        })
        throw new Error('需登录')
      }

      return new Promise((resolve, reject) => {
        globalModel.update({
          loginDialogVisible: true,
          loginDialogSuccessCallbacks: [
            ...globalModel.state.loginDialogSuccessCallbacks,
            (p: string) => {
              setP(p)
              retry().then(resolve, reject)
            },
          ] as any,
          loginDialogCancelCallbacks: [
            ...globalModel.state.loginDialogCancelCallbacks,
            reject,
          ] as any,
        })
      })
    }

    if (data.success === false) {
      const errorMessageMap = options?.errorMessageMap || {}
      const statusMessageMap = Object.keys(errorMessageMap).reduce<
        Record<number, string>
      >((res, message) => {
        castArray(errorMessageMap[message]).forEach(status => {
          res[status] = message
        })
        return res
      }, {})
      const errorMessage =
        statusMessageMap[data.status] || statusMessageMap['-1'] || data.msg
      if (
        errorMessage === '用户信息不匹配' ||
        errorMessage === '用户信息错误' ||
        data.status === 4030
      ) {
        globalModel.update({
          isAccessDenied: true,
        })
      } else if (!options?.noErrorToast) {
        Toast.clear(true)
        Toast({
          message: errorMessage,
          forbidClick: false,
          mask: false,
        })
      }
      throw new Error(errorMessage)
    }
  }
  apiBus.emit('requestSuccess', payload.requestFunctionName as any)

  const actualData =
    isPlainObject(data) && payload.dataKey && has(data, payload.dataKey)
      ? data[payload.dataKey as any]
      : data

  if (isPlainObject(actualData)) {
    traverse(actualData, (value, key, parent) => {
      // 强制兼容 lowestPrice
      if (
        key === 'lowestPrice' &&
        // 仅 price 无值时取 lowestPrice
        parent.price == null
      ) {
        parent.price = value
      }

      // 强制转换图片链接到 CDN
      if (typeof key === 'string' && typeof value === 'string') {
        parent[key] = value.replace(
          /dcb-resource\.oss-cn-chengdu\.aliyuncs\.com/g,
          'dcb-resource.gogpay.cn',
        )
      }
    })
  }

  return actualData
}
